import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useSubscription } from '../../../hooks/useSubscription';
import { useCan } from '../../../hooks/useCan';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';

export function NudgeBanner() {
  const { t } = useTranslation('billing');
  const { data } = useSubscription();
  const openUpgrade = useUpgradeDialogStore((s) => s.open);
  // Renewal nudge routes into billing (manage:settings). MEMBER has no billing
  // access, so don't surface it to them.
  const canManageBilling = useCan('manage:settings');
  const nudge = canManageBilling ? (data?.nudge ?? null) : null;
  const dismissKey = nudge ? `nudge-dismissed:${nudge.kind}:${nudge.dueAt}` : '';
  const [dismissed, setDismissed] = useState(
    () => !!nudge && typeof window !== 'undefined' && localStorage.getItem(dismissKey) === '1',
  );
  // THI-200 follow-up: OfferBanner (mounted above this one) already carries
  // the trial's days-left in its own countdown chip when an offer is
  // present — a TRIALING nudge here would just repeat it in a second frame.
  if (!nudge || dismissed || (nudge.kind === 'TRIALING' && data?.offer)) return null;

  const planName = data?.plan.name ?? '';
  const days = nudge.daysLeft;
  // Every nudge kind is a heads-up, not a failure — pricing v3 dropped the
  // lock states that warranted a red banner.
  const tone = 'bg-amber-50 border-amber-200 text-amber-800';

  const targetId = data?.pendingInvoice?.id ?? null;
  const ctaTo = targetId ? `/settings/billing/invoices/${targetId}` : `/settings/billing`;

  return (
    <div className={`flex items-center justify-between gap-3 border-b px-5 py-2 text-[13px] ${tone}`}>
      <div className="flex-1">
        <span className="font-semibold mr-2">{t(`nudge.${nudge.kind}.title`, { plan: planName, days })}</span>
        <span>{t(`nudge.${nudge.kind}.body`, { plan: planName, days })}</span>
        {/* THI-200 follow-up: the offer sentence (with its plan-coverage
            caveat) moved to OfferBanner, mounted above this one — repeating
            it here duplicated the same fact in two adjacent frames. */}
      </div>
      {nudge.kind === 'TRIALING' || nudge.kind === 'DOWNGRADED' || nudge.kind === 'TRIAL_ENDED' ? (
        // None of the three has a specific pending invoice to point at
        // (DOWNGRADED/TRIAL_ENDED's whole point is that nothing is pending —
        // the plan already lapsed or the trial already ended), so all three
        // open the upgrade dialog directly rather than routing through
        // /settings/billing first.
        <button type="button" onClick={() => openUpgrade()} className="font-semibold underline">
          {t(`nudge.${nudge.kind}.cta`)}
        </button>
      ) : (
        <Link to={ctaTo} className="font-semibold underline">
          {t(`nudge.${nudge.kind}.cta`)}
        </Link>
      )}
      <button
        type="button"
        onClick={() => {
          localStorage.setItem(dismissKey, '1');
          setDismissed(true);
        }}
        className="opacity-60 hover:opacity-100"
        aria-label={t('nudge.dismiss')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
